import React, { useEffect, useRef, useState } from "react";
import RichTextEditor from "@/components/RichTextEditor";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X as XIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const MASTERS = [
  { key: "designation", label: "Body Designation", desc: "Designations for Executive & General Body members (President, Secretary, Treasurer, etc.). Each entry has an Authority Level (1 = top → 10 = bottom) used to arrange members in lists. Note: For institute staff designations use the 'Staff Designation' tab.", isBodyDesignation: true },
  { key: "staff_designation", label: "Staff Designation", desc: "Designations for institute staff, organised under Teaching and Non-teaching staff types.", isStaffDesignation: true },
  { key: "institute_type", label: "Institute Type", desc: "Classification of institutes" },
  { key: "bank_purpose", label: "Bank Purpose", desc: "Account purpose dropdown" },
  { key: "bank_account_type", label: "Bank Account Type", desc: "Account type dropdown used in Bank Accounts (Savings, Current, Fixed Deposit, etc.)" },
  { key: "unit_type", label: "Unit Type", desc: "Property unit categories (Room, Lab, Shop, Office, Classroom, Hostel Room, Hall, etc.) used in Properties → Units" },
  { key: "financial_doc_type", label: "Financial Document Type", desc: "Types of financial filings (Audit Report, Balance Sheet, Form 10B, etc.) used in Add Financial Document" },
  { key: "document_category", label: "Document Category", desc: "Categories used for trust official documents" },
  { key: "related_entity", label: "Related Entity", desc: "Entities a document relates to (Charity Commissioner, AICTE, etc.)" },
  { key: "asset_type", label: "Asset Type", desc: "Immovable asset classifications (Land, Building, Shop, Flat, etc.)" },
  { key: "gallery_type", label: "Gallery Type", desc: "Photo gallery categories (Events, Cultural, Sports, Alumni, etc.)" },
  { key: "employee_category", label: "Employee Category", desc: "Categories for trust employees (Teaching, Non-teaching, etc.)" },
  { key: "qualification", label: "Qualification", desc: "Highest qualification dropdown for employees (Ph.D., M.Tech, B.E., etc.)" },
  { key: "campus_engagement_type", label: "Campus Engagement Type", desc: "Types used by institute Campus Engagements (Event, Achievement, Workshop, Seminar, etc.)" },
  { key: "committee_type", label: "Committee / Cell Type", desc: "Names of statutory/functional committees & cells (Anti-Ragging, IQAC, ICC, Grievance, Placement, etc.)" },
  { key: "movable_item_category", label: "Movable Item Category", desc: "Top-level categories for institute movable inventory (Furniture, Electronics, Lab Equipment, etc.)" },
  { key: "movable_item_subcategory", label: "Movable Item Subcategory", desc: "Subcategories under a parent category (e.g. Chair under Furniture, Computer under Electronics). Each entry must be linked to a parent category.", isParented: true, parentMaster: "movable_item_category" },
  { key: "immovable_item_category", label: "Immovable Item Category", desc: "Top-level categories for fixed / immovable items inside a Trust Estate unit (Civil Structure, Electrical Fixtures, Plumbing, etc.)" },
  { key: "immovable_item_subcategory", label: "Immovable Item Subcategory", desc: "Items under an immovable category (e.g. Ceiling Fan under Electrical Fixtures, Door under Civil Structure). Each entry must be linked to a parent category.", isParented: true, parentMaster: "immovable_item_category" },
  { key: "tender_type", label: "Tender · Type", desc: "Tender types used in Tender Management (Open Tender, Limited Tender, EOI, RFP, RFQ, Single Tender, etc.)" },
  { key: "tender_category", label: "Tender · Category", desc: "Top-level classification used by both Tender Management and Vendor Empanelment (Contractor, Manufacturer, Service Provider, Consultant, etc.). Subcategories are grouped under each Category." },
  { key: "tender_subcategory", label: "Tender · Subcategory", desc: "Subcategory / discipline within a specific Tender Category (e.g. under Contractor → Civil Works, Electrical, Plumbing). Each entry must be linked to a parent Tender Category. Vendors can pick multiple subcategories on the empanelment form.", isParented: true, parentMaster: "tender_category" },
  { key: "amenity_type", label: "Amenity Type", desc: "Types shown in the Amenity Type dropdown when creating or editing amenities (Sports, Library, Auditorium, Hostel, Guest House, Canteen, etc.). Hostel and Guest House automatically switch bookings to a check-in / check-out flow. Cannot delete a type that is still used by any amenity." },
  { key: "vendor_registration_fee", label: "Vendor Registration Fee", desc: "One-time empanelment fee charged when a vendor registers on GS Mandal. Only one entry is stored. Tick 'Registration fee is waived off' to disable the charge; otherwise enter the fee amount in INR.", isVendorRegistrationFee: true },
  { key: "marathon_event", label: "Marathon Event", desc: "Name and details of the marathon shown in the header of the public run registration page (/run/register), and on the receipt and confirmation email. Only one entry is stored.", isMarathonEvent: true, superAdminOnly: true },
  { key: "show_fees", label: "Show Fees", desc: "Masters used across the institute fee-structure builder — Category (Caste), Course Type, Fees Header components, and Admission Process types.", isShowFees: true },
];

const SHOW_FEES_MASTERS = [
  { key: "fee_category", label: "Category (Caste)", helper: "Caste-based fee categories used in fee structures (Open / SC / ST / OBC / VJNT / SBC / EWS / Minority, etc.)." },
  { key: "fee_course_type", label: "Course Type", helper: "Course/programme classifications used in fee tables (e.g. Regular, Lateral Entry, Direct Second Year, Part-time)." },
  { key: "fee_header", label: "Fees Header", helper: "Line-items that make up the fee structure (Tuition Fee, Development Fee, Library, Lab, Exam Fee, Hostel, etc.)." },
  { key: "fee_admission_process", label: "Admission Process", helper: "Admission routes shown against fee tables (CAP Round, Institute Level, Management Quota, Direct, JEE, MHT-CET, etc.)." },
];

function ShowFeesSection() {
  const [active, setActive] = React.useState(SHOW_FEES_MASTERS[0].key);
  const activeMaster = SHOW_FEES_MASTERS.find((m) => m.key === active) || SHOW_FEES_MASTERS[0];
  return (
    <div className="space-y-4" data-testid="master-show-fees-section">
      <div className="flex flex-wrap gap-2" data-testid="master-show-fees-pills">
        {SHOW_FEES_MASTERS.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setActive(m.key)}
            data-testid={`master-show-fees-pill-${m.key}`}
            className={`px-4 py-2 text-xs tracking-wide uppercase border rounded-sm transition-colors ${
              active === m.key
                ? "bg-[#234333] text-white border-[#234333]"
                : "bg-white text-[#6b655b] border-[#e5e0d8] hover:bg-[#efece6]"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="text-xs text-[#6b655b] italic">{activeMaster.helper}</div>
      <MasterList key={active} master={active} />
    </div>
  );
}

const STAFF_TYPES = [
  { key: "teaching", label: "Teaching" },
  { key: "non_teaching", label: "Non-teaching" },
];

function StaffDesignationList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("teaching");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const fetchItems = async () => {
    const { data } = await api.get(`/masters/staff_designation`);
    setItems(data);
  };
  useEffect(() => {
    setLoading(true);
    fetchItems().finally(() => setLoading(false));
  }, []);

  const filtered = items
    .filter((i) => i.staff_type === activeType)
    .sort((a, b) => a.name.localeCompare(b.name));

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post(`/masters/staff_designation`, {
        name,
        staff_type: activeType,
      });
      setItems((arr) => [...arr, data]);
      setNewName("");
      toast.success("Added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Add failed");
    }
  };

  const saveEdit = async () => {
    const name = editingName.trim();
    if (!name) return;
    try {
      const item = items.find((x) => x.id === editingId);
      const { data } = await api.put(`/masters/staff_designation/${editingId}`, {
        name,
        staff_type: item?.staff_type || activeType,
      });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      setEditingId(null);
      setEditingName("");
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/masters/staff_designation/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm">
      {/* Staff type sub-tabs */}
      <div
        className="flex border-b border-[#e5e0d8]"
        data-testid="staff-designation-types"
      >
        {STAFF_TYPES.map((t) => {
          const count = items.filter((i) => i.staff_type === t.key).length;
          return (
            <button
              key={t.key}
              onClick={() => setActiveType(t.key)}
              data-testid={`staff-type-${t.key}`}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeType === t.key
                  ? "border-[#234333] text-[#234333] bg-[#f9f8f6]"
                  : "border-transparent text-[#6b655b] hover:text-[#1c1a17]"
              }`}
            >
              {t.label}
              <span className="ml-2 text-[10px] text-[#6b655b]">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="p-5 border-b border-[#e5e0d8] flex gap-3">
        <input
          data-testid={`staff-designation-${activeType}-new-input`}
          className={inputCls + " flex-1"}
          placeholder={`Add new ${activeType.replace("_", "-")} designation…`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button
          onClick={add}
          data-testid={`staff-designation-${activeType}-add-button`}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm"
        >
          <Plus size={14} strokeWidth={1.5} /> Add
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-[#6b655b] text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-[#6b655b] text-sm">
          No {activeType.replace("_", "-")} designations yet.
        </div>
      ) : (
        <ul className="divide-y divide-[#e5e0d8]">
          {filtered.map((it) => (
            <li
              key={it.id}
              data-testid={`staff-designation-item-${it.id}`}
              className="flex items-center justify-between px-5 py-3 hover:bg-[#f9f8f6]/60"
            >
              {editingId === it.id ? (
                <>
                  <input
                    data-testid={`staff-designation-edit-input-${it.id}`}
                    className={inputCls + " mr-3"}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    autoFocus
                  />
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={saveEdit}
                      data-testid={`staff-designation-save-${it.id}`}
                      className="p-2 text-[#3a5a40] hover:bg-[#efece6] rounded-sm"
                    >
                      <Check size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                      }}
                      className="p-2 text-[#6b655b] hover:bg-[#efece6] rounded-sm"
                    >
                      <XIcon size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-sm text-[#1c1a17]">{it.name}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingId(it.id);
                        setEditingName(it.name);
                      }}
                      data-testid={`staff-designation-edit-${it.id}`}
                      className="p-2 text-[#234333] hover:bg-[#efece6] rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      data-testid={`staff-designation-delete-${it.id}`}
                      className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BodyDesignationList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newLevel, setNewLevel] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingLevel, setEditingLevel] = useState(10);

  const fetchItems = async () => {
    const { data } = await api.get(`/masters/designation`);
    setItems(data);
  };
  useEffect(() => {
    setLoading(true);
    fetchItems().finally(() => setLoading(false));
  }, []);

  // Already sorted server-side by authority_level then name
  const sorted = items;

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post(`/masters/designation`, {
        name,
        authority_level: Number(newLevel),
      });
      setItems((arr) =>
        [...arr, data].sort(
          (a, b) =>
            (a.authority_level || 10) - (b.authority_level || 10) ||
            a.name.localeCompare(b.name),
        ),
      );
      setNewName("");
      setNewLevel(10);
      toast.success("Added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Add failed");
    }
  };

  const saveEdit = async () => {
    const name = editingName.trim();
    if (!name) return;
    try {
      const { data } = await api.put(`/masters/designation/${editingId}`, {
        name,
        authority_level: Number(editingLevel),
      });
      setItems((arr) =>
        arr
          .map((x) => (x.id === data.id ? data : x))
          .sort(
            (a, b) =>
              (a.authority_level || 10) - (b.authority_level || 10) ||
              a.name.localeCompare(b.name),
          ),
      );
      setEditingId(null);
      setEditingName("");
      setEditingLevel(10);
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/masters/designation/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  return (
    <div
      className="bg-white border border-[#e5e0d8] rounded-sm"
      data-testid="body-designation-section"
    >
      <div className="p-5 border-b border-[#e5e0d8] grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3">
        <input
          data-testid="body-designation-new-input"
          className={inputCls}
          placeholder="Add new designation (e.g., President)…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <select
          data-testid="body-designation-new-level"
          className={inputCls}
          value={newLevel}
          onChange={(e) => setNewLevel(Number(e.target.value))}
          title="Authority Level (1 = top, 10 = bottom)"
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              Level {l}
              {l === 1 ? " (Top)" : l === 10 ? " (Bottom)" : ""}
            </option>
          ))}
        </select>
        <button
          onClick={add}
          data-testid="body-designation-add-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm whitespace-nowrap"
        >
          <Plus size={14} strokeWidth={1.5} /> Add
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-[#6b655b] text-sm">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="p-8 text-[#6b655b] text-sm" data-testid="body-designation-empty">
          No designations yet. Add one above.
        </div>
      ) : (
        <ul
          className="divide-y divide-[#e5e0d8]"
          data-testid="body-designation-list"
        >
          {sorted.map((it) => (
            <li
              key={it.id}
              className="flex items-center justify-between px-5 py-3 hover:bg-[#f9f8f6]/60"
              data-testid={`body-designation-item-${it.id}`}
            >
              {editingId === it.id ? (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3">
                  <input
                    data-testid={`body-designation-edit-input-${it.id}`}
                    className={inputCls}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    autoFocus
                  />
                  <select
                    data-testid={`body-designation-edit-level-${it.id}`}
                    className={inputCls}
                    value={editingLevel}
                    onChange={(e) => setEditingLevel(Number(e.target.value))}
                  >
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>
                        Level {l}
                        {l === 1 ? " (Top)" : l === 10 ? " (Bottom)" : ""}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={saveEdit}
                      data-testid={`body-designation-save-${it.id}`}
                      className="p-2 text-[#3a5a40] hover:bg-[#efece6] rounded-sm"
                    >
                      <Check size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                        setEditingLevel(10);
                      }}
                      data-testid={`body-designation-cancel-${it.id}`}
                      className="p-2 text-[#6b655b] hover:bg-[#efece6] rounded-sm"
                    >
                      <XIcon size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex items-center justify-center min-w-[56px] h-6 text-[10px] tracking-wide uppercase bg-[#234333]/5 border border-[#234333]/20 text-[#234333] rounded-sm px-2 font-mono">
                      L{it.authority_level ?? 10}
                    </span>
                    <span className="text-sm text-[#1c1a17] truncate">{it.name}</span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => {
                        setEditingId(it.id);
                        setEditingName(it.name);
                        setEditingLevel(it.authority_level ?? 10);
                      }}
                      data-testid={`body-designation-edit-${it.id}`}
                      className="p-2 text-[#234333] hover:bg-[#efece6] rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      data-testid={`body-designation-delete-${it.id}`}
                      className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ParentedMasterList({ master, parentMaster }) {
  const [items, setItems] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeParent, setActiveParent] = useState("");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingParent, setEditingParent] = useState("");

  const fetchAll = async () => {
    const [a, b] = await Promise.all([
      api.get(`/masters/${master}`),
      api.get(`/masters/${parentMaster}`),
    ]);
    setItems(a.data);
    setParents(b.data);
    if (!activeParent && b.data.length) setActiveParent(b.data[0].name);
  };
  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, [master, parentMaster]);

  const filtered = items
    .filter((i) => i.parent === activeParent)
    .sort((a, b) => a.name.localeCompare(b.name));

  const add = async () => {
    const name = newName.trim();
    if (!name || !activeParent) return;
    try {
      const { data } = await api.post(`/masters/${master}`, { name, parent: activeParent });
      setItems((arr) => [...arr, data]);
      setNewName("");
      toast.success("Added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Add failed");
    }
  };

  const saveEdit = async () => {
    const name = editingName.trim();
    if (!name || !editingParent) return;
    try {
      const { data } = await api.put(`/masters/${master}/${editingId}`, {
        name,
        parent: editingParent,
      });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      setEditingId(null);
      setEditingName("");
      setEditingParent("");
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/masters/${master}/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  if (parents.length === 0) {
    return (
      <div className="bg-white border border-[#e5e0d8] rounded-sm p-8 text-sm text-[#6b655b]">
        Add at least one entry in the parent master &ldquo;<b>{(MASTERS.find((m) => m.key === parentMaster) || {}).label || parentMaster}</b>&rdquo; first.
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm">
      <div className="p-5 border-b border-[#e5e0d8] flex flex-wrap gap-2">
        {parents.map((p) => (
          <button
            key={p.id}
            data-testid={`master-${master}-parent-${p.name}`}
            onClick={() => setActiveParent(p.name)}
            className={`px-3 py-1.5 text-xs tracking-wide uppercase border rounded-sm ${
              activeParent === p.name
                ? "bg-[#234333] text-white border-[#234333]"
                : "bg-white text-[#6b655b] border-[#e5e0d8] hover:bg-[#efece6]"
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>

      <div className="p-5 border-b border-[#e5e0d8] flex gap-3">
        <input
          data-testid={`master-${master}-new-input`}
          className={inputCls + " flex-1"}
          placeholder={`Add subcategory under "${activeParent}"…`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button
          onClick={add}
          data-testid={`master-${master}-add-button`}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm"
        >
          <Plus size={14} strokeWidth={1.5} /> Add
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-[#6b655b] text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-[#6b655b] text-sm" data-testid={`master-${master}-empty`}>
          No subcategories under &ldquo;{activeParent}&rdquo; yet. Add one above.
        </div>
      ) : (
        <ul className="divide-y divide-[#e5e0d8]" data-testid={`master-${master}-list`}>
          {filtered.map((it) => (
            <li
              key={it.id}
              className="flex items-center justify-between px-5 py-3 hover:bg-[#f9f8f6]/60"
              data-testid={`master-${master}-item-${it.id}`}
            >
              {editingId === it.id ? (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3">
                  <input
                    className={inputCls}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    autoFocus
                  />
                  <select
                    className={inputCls}
                    value={editingParent}
                    onChange={(e) => setEditingParent(e.target.value)}
                  >
                    {parents.map((p) => (
                      <option key={p.id} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={saveEdit}
                      className="p-2 text-[#3a5a40] hover:bg-[#efece6] rounded-sm"
                    >
                      <Check size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                        setEditingParent("");
                      }}
                      className="p-2 text-[#6b655b] hover:bg-[#efece6] rounded-sm"
                    >
                      <XIcon size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="text-sm text-[#1c1a17]">{it.name}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingId(it.id);
                        setEditingName(it.name);
                        setEditingParent(it.parent || activeParent);
                      }}
                      className="p-2 text-[#234333] hover:bg-[#efece6] rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MasterList({ master }) {
  const orderable = master === "fee_header" || master === "fee_course_type";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newOrder, setNewOrder] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingOrder, setEditingOrder] = useState("");

  const sortItems = (arr) => {
    if (orderable) {
      return [...arr].sort((a, b) =>
        ((a.order ?? 999) - (b.order ?? 999)) || a.name.localeCompare(b.name)
      );
    }
    return [...arr].sort((a, b) => a.name.localeCompare(b.name));
  };

  const fetchItems = async () => {
    const { data } = await api.get(`/masters/${master}`);
    setItems(sortItems(data));
  };

  useEffect(() => {
    setLoading(true);
    fetchItems().finally(() => setLoading(false));
  }, [master]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const payload = { name };
      if (orderable) payload.order = newOrder === "" ? (items.length + 1) : Number(newOrder);
      const { data } = await api.post(`/masters/${master}`, payload);
      setItems((arr) => sortItems([...arr, data]));
      setNewName("");
      setNewOrder("");
      toast.success("Entry added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Add failed");
    }
  };

  const saveEdit = async () => {
    const name = editingName.trim();
    if (!name) return;
    try {
      const payload = { name };
      if (orderable) payload.order = editingOrder === "" ? 999 : Number(editingOrder);
      const { data } = await api.put(`/masters/${master}/${editingId}`, payload);
      setItems((arr) => sortItems(arr.map((x) => (x.id === data.id ? data : x))));
      setEditingId(null);
      setEditingName("");
      setEditingOrder("");
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/masters/${master}/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm">
      <div className="p-5 border-b border-[#e5e0d8] flex gap-3">
        {orderable && (
          <input
            data-testid={`master-${master}-new-order`}
            type="number"
            className={inputCls + " w-24"}
            placeholder="Order"
            value={newOrder}
            onChange={(e) => setNewOrder(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            title="Display order (lower = earlier)"
          />
        )}
        <input
          data-testid={`master-${master}-new-input`}
          className={inputCls + " flex-1"}
          placeholder={orderable ? (master === "fee_header" ? "Fee head name (e.g. Tuition Fee)" : "Course type name (e.g. First Year - Regular)") : "Add new entry…"}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button
          onClick={add}
          data-testid={`master-${master}-add-button`}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm"
        >
          <Plus size={14} strokeWidth={1.5} /> Add
        </button>
      </div>

      {orderable && (
        <div className="px-5 py-2 bg-[#faf7f0] border-b border-[#e5e0d8] text-[11px] text-[#6b655b] italic">
          Display order is used to arrange {master === "fee_header" ? "fee heads" : "course types"} consistently across the Fee Structure Builder (admin) and the public Know-Your-Fees page. Lower number appears first.
        </div>
      )}

      {loading ? (
        <div className="p-8 text-[#6b655b] text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-[#6b655b] text-sm" data-testid={`master-${master}-empty`}>
          No entries yet. Add one above.
        </div>
      ) : (
        <ul className="divide-y divide-[#e5e0d8]" data-testid={`master-${master}-list`}>
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-center justify-between px-5 py-3 hover:bg-[#f9f8f6]/60"
              data-testid={`master-${master}-item-${it.id}`}
            >
              {editingId === it.id ? (
                <>
                  {orderable && (
                    <input
                      data-testid={`master-${master}-edit-order-${it.id}`}
                      type="number"
                      className={inputCls + " w-24 mr-2"}
                      value={editingOrder}
                      onChange={(e) => setEditingOrder(e.target.value)}
                    />
                  )}
                  <input
                    data-testid={`master-${master}-edit-input-${it.id}`}
                    className={inputCls + " mr-3 flex-1"}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    autoFocus
                  />
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={saveEdit}
                      data-testid={`master-${master}-save-${it.id}`}
                      className="p-2 text-[#3a5a40] hover:bg-[#efece6] rounded-sm"
                    >
                      <Check size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                        setEditingOrder("");
                      }}
                      data-testid={`master-${master}-cancel-${it.id}`}
                      className="p-2 text-[#6b655b] hover:bg-[#efece6] rounded-sm"
                    >
                      <XIcon size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-3 text-sm text-[#1c1a17]">
                    {orderable && (
                      <span className="inline-flex items-center justify-center min-w-[36px] h-6 text-[11px] font-mono font-bold text-white bg-[#234333] rounded-sm px-1.5" data-testid={`master-${master}-order-badge-${it.id}`}>#{it.order ?? 999}</span>
                    )}
                    <span>{it.name}</span>
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingId(it.id);
                        setEditingName(it.name);
                        setEditingOrder(it.order ?? "");
                      }}
                      data-testid={`master-${master}-edit-${it.id}`}
                      className="p-2 text-[#234333] hover:bg-[#efece6] rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      data-testid={`master-${master}-delete-${it.id}`}
                      className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Vendor Registration Fee — singleton config (only one entry, editable in place)
function VendorRegistrationFeeSection() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState("");
  const [isWaived, setIsWaived] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vendor-registration-fee");
      setDoc(data);
      setAmount(data.is_waived ? "" : String(data.amount || ""));
      setIsWaived(!!data.is_waived);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { is_waived: isWaived, amount: isWaived ? 0 : parseFloat(amount || 0) };
      const { data } = await api.put("/admin/vendor-registration-fee", payload);
      setDoc(data);
      setEditing(false);
      toast.success("Vendor registration fee updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-[#6b655b]" data-testid="vrf-loading">Loading…</div>;

  const displayAmount = doc && !doc.is_waived
    ? `₹ ${Number(doc.amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
    : "Waived off";

  return (
    <div className="max-w-xl" data-testid="vrf-section">
      {!editing ? (
        <div className="bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">Current setting</div>
              <div className="mt-1 font-serif text-2xl text-[#1c1a17]" data-testid="vrf-current-amount">
                {displayAmount}
              </div>
              {doc?.is_waived && (
                <div className="text-[11px] text-[#6b655b] mt-1">Vendors are not charged any registration fee.</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setAmount(doc && !doc.is_waived ? String(doc.amount) : ""); setIsWaived(doc?.is_waived ?? true); setEditing(true); }}
              data-testid="vrf-edit"
              className="inline-flex items-center gap-1 bg-white border border-[#234333] hover:bg-[#234333] hover:text-white text-[#234333] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors"
            >
              <Pencil size={11} /> Edit
            </button>
          </div>
          {doc?.updated_at && (
            <div className="text-[10px] text-[#8a8478] pt-2 border-t border-[#e5e0d8]">
              Last updated: {new Date(doc.updated_at).toLocaleString()} {doc.updated_by ? `· by ${doc.updated_by}` : ""}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#fbf8f1] border border-[#234333]/40 rounded-sm p-5 space-y-4" data-testid="vrf-edit-form">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[#234333]"
              checked={isWaived}
              onChange={(e) => setIsWaived(e.target.checked)}
              data-testid="vrf-waived-checkbox"
            />
            <span>
              <span className="font-semibold text-[#1c1a17]">Registration fee is waived off</span>
              <span className="block text-[11px] text-[#6b655b] mt-0.5">Tick this to disable the empanelment fee for all vendors.</span>
            </span>
          </label>

          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mb-1.5">Registration Fee (INR)</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#6b655b]">₹</span>
              <input
                type="number"
                min="0"
                step="1"
                className={`${inputCls} ${isWaived ? "bg-[#f5f1e8] opacity-60" : ""}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={isWaived}
                placeholder="e.g. 2000"
                data-testid="vrf-amount"
              />
            </div>
            {!isWaived && (
              <div className="text-[10px] text-[#8a8478] mt-1">Enter the one-time empanelment fee vendors will pay while registering.</div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#e5e0d8]">
            <button type="button" onClick={() => { setEditing(false); load(); }} className="bg-white border border-[#e5e0d8] text-[#6b655b] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm" data-testid="vrf-cancel">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving} className="bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-sm disabled:opacity-60 inline-flex items-center gap-1" data-testid="vrf-save">
              {saving ? "Saving…" : (<><Check size={11} /> Save</>)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Marathon Event — singleton name + details shown on /run/register header
function MarathonEventSection() {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", cancellation_policy: "", privacy_policy: "" });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const pick = (d) => ({ name: d.name, description: d.description, cancellation_policy: d.cancellation_policy || "", privacy_policy: d.privacy_policy || "" });
  const bannerRef = useRef(null);

  const pickBanner = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) return toast.error("Banner: only JPG, PNG or WEBP images are allowed");
    if (f.size === 0) return toast.error("Banner: the file is empty (0 KB)");
    if (f.size > 3 * 1024 * 1024) return toast.error("Banner: image must be 3 MB or smaller");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/admin/marathon/event/banner", fd);
      setDoc(data);
      toast.success("Banner image updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || `Upload failed${err?.response?.status ? ` (HTTP ${err.response.status})` : ""}`);
    } finally { setUploading(false); }
  };

  const removeBanner = async () => {
    if (!window.confirm("Remove the banner image? The default photo will be shown.")) return;
    try {
      const { data } = await api.delete("/admin/marathon/event/banner");
      setDoc(data);
      toast.success("Banner image removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Remove failed");
    }
  };

  const bannerBlock = doc && (
    <div className="max-w-2xl bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-5 mt-4" data-testid="me-banner-section">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mb-2">Header banner image</div>
      <div className="relative aspect-[12/5] rounded-sm overflow-hidden bg-[#08363A]">
        {doc.banner_url ? (
          <>
            <img src={doc.banner_url} alt="Marathon banner" className="absolute inset-0 w-full h-full object-cover opacity-30" data-testid="me-banner-preview" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#08363A] via-[#08363A]/85 to-transparent" />
            <div className="absolute left-4 bottom-4 text-white font-bold uppercase text-xl">{doc.name || "Run Registration"}</div>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm" data-testid="me-banner-empty">No banner uploaded, so the default photo is used</div>
        )}
      </div>
      <div className="text-[10px] text-[#8a8478] mt-2">Preview with the page tint. JPG, PNG or WEBP · max 3 MB · wide image recommended (1920×800).</div>
      <input ref={bannerRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" onChange={pickBanner} data-testid="me-banner-input" />
      <div className="flex gap-2 mt-3">
        <button type="button" onClick={() => bannerRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1 bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm disabled:opacity-60" data-testid="me-banner-upload">
          {uploading ? "Uploading…" : doc.banner_url ? "Replace image" : "Upload image"}
        </button>
        {doc.banner_url && (
          <button type="button" onClick={removeBanner} className="bg-white border border-red-200 text-red-700 hover:bg-red-50 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm" data-testid="me-banner-remove">Remove</button>
        )}
      </div>
    </div>
  );

  useEffect(() => {
    api.get("/admin/marathon/event").then(({ data }) => { setDoc(data); setForm(pick(data)); })
      .catch((err) => toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/marathon/event", form);
      setDoc(data); setEditing(false);
      toast.success("Marathon event updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!doc) return <div className="text-sm text-[#6b655b]" data-testid="me-loading">Loading…</div>;
  if (!editing) {
    return (
      <>
      <div className="max-w-2xl bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-5" data-testid="me-section">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">Marathon name</div>
            <div className="mt-1 font-serif text-2xl text-[#1c1a17]" data-testid="me-current-name">{doc.name || "Not set"}</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mt-4">Details</div>
            <div className="mt-1 text-sm text-[#3a362f] whitespace-pre-line" data-testid="me-current-description">{doc.description || "—"}</div>
            {[["Cancellation and Refund Policy", "cancellation_policy"], ["Privacy Policy", "privacy_policy"]].map(([label, k]) => (
              <div key={k}>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mt-4">{label}</div>
                {doc[k] ? <div className="mt-1 prose prose-sm max-w-none max-h-40 overflow-y-auto" data-testid={`me-current-${k}`} dangerouslySetInnerHTML={{ __html: doc[k] }} /> : <div className="mt-1 text-sm text-[#8a8478]" data-testid={`me-current-${k}`}>Not published</div>}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setEditing(true)} data-testid="me-edit" className="inline-flex items-center gap-1 bg-white border border-[#234333] hover:bg-[#234333] hover:text-white text-[#234333] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors">
            <Pencil size={11} /> Edit
          </button>
        </div>
        {doc.updated_at && <div className="text-[10px] text-[#8a8478] pt-2 mt-3 border-t border-[#e5e0d8]">Last updated: {new Date(doc.updated_at).toLocaleString()} {doc.updated_by ? `· by ${doc.updated_by}` : ""}</div>}
      </div>
      {bannerBlock}
      </>
    );
  }
  return (
    <>
    <div className="max-w-3xl bg-[#fbf8f1] border border-[#234333]/40 rounded-sm p-5 space-y-4" data-testid="me-edit-form">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mb-1.5">Marathon name</div>
        <input className={inputCls} value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Run for the community" data-testid="me-name" />
        <div className="text-[10px] text-[#8a8478] mt-1">Shown as the big header title. The last word is highlighted.</div>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mb-1.5">Details of the marathon</div>
        <textarea rows={4} className={inputCls} value={form.description} maxLength={800} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="me-description" />
        <div className="text-[10px] text-[#8a8478] mt-1">{form.description.length}/800</div>
      </div>
      {[["Cancellation and Refund Policy", "cancellation_policy"], ["Privacy Policy", "privacy_policy"]].map(([label, k]) => (
        <div key={k}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mb-1.5">{label}</div>
          <RichTextEditor value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} placeholder={`${label} shown on the run registration page…`} testid={`me-${k}`} />
        </div>
      ))}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#e5e0d8]">
        <button type="button" onClick={() => { setEditing(false); setForm(pick(doc)); }} className="bg-white border border-[#e5e0d8] text-[#6b655b] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm" data-testid="me-cancel">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-sm disabled:opacity-60 inline-flex items-center gap-1" data-testid="me-save">
          {saving ? "Saving…" : (<><Check size={11} /> Save</>)}
        </button>
      </div>
    </div>
    {bannerBlock}
    </>
  );
}

// Master types that the Infrastructure role is allowed to manage.
const INFRA_MASTER_KEYS = new Set(["tender_type", "tender_category", "tender_subcategory", "vendor_registration_fee", "amenity_type"]);

export default function Masters() {
  const { user } = useAuth();
  const isInfra = user?.role === "infrastructure";
  const visibleMasters = (isInfra ? MASTERS.filter((m) => INFRA_MASTER_KEYS.has(m.key)) : MASTERS)
    .filter((m) => !m.superAdminOnly || user?.role === "super_admin");
  const defaultTab = visibleMasters[0]?.key || "designation";
  return (
    <div className="p-8 md:p-12 max-w-4xl mx-auto" data-testid="masters-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Lookups</div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Master Data</h1>
        <p className="mt-2 text-sm text-[#6b655b]">
          Manage dropdown values used across the trust management system.
        </p>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-transparent p-0 border-b border-[#e5e0d8] rounded-none h-auto w-full justify-start gap-4 flex-wrap">
          {visibleMasters.map((m) => (
            <TabsTrigger
              key={m.key}
              value={m.key}
              data-testid={`master-tab-${m.key}`}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#234333] data-[state=active]:border-b-2 data-[state=active]:border-[#234333] rounded-none px-0 py-3 text-sm font-sans text-[#6b655b] shadow-none"
            >
              {m.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleMasters.map((m) => (
          <TabsContent key={m.key} value={m.key} className="pt-6">
            <div className="mb-4 text-sm text-[#6b655b]">{m.desc}</div>
            {m.isStaffDesignation ? (
              <StaffDesignationList />
            ) : m.isBodyDesignation ? (
              <BodyDesignationList />
            ) : m.isShowFees ? (
              <ShowFeesSection />
            ) : m.isVendorRegistrationFee ? (
              <VendorRegistrationFeeSection />
            ) : m.isMarathonEvent ? (
              <MarathonEventSection />
            ) : m.isParented ? (
              <ParentedMasterList master={m.key} parentMaster={m.parentMaster} />
            ) : (
              <MasterList master={m.key} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
